import React from 'react'
import { useSelector } from 'react-redux'
import { selectMultipleCollations } from 'actions/ui'
import { getShipmentTours } from 'selectors/combined'
import { getSelectedTourId, getHiddenTourIds } from 'selectors/tour'
import * as fromGreenRegions from 'selectors/greenRegions'
import { moveSingle, setHiddenTourIds, setSelectedTourId } from 'actions/tours'

import { ShipmentTour } from 'common/models/typings/Tour'
import { Collation } from 'common/models/typings/Collation'
import { DeckGL } from '@deck.gl/react/typed'
import {
  MapView,
  MapViewState,
  WebMercatorViewport,
  PickingInfo,
  Color,
} from '@deck.gl/core/typed'
import { getSelectedCollations } from 'selectors/ui'
import { isValidCollation } from 'actions/tours/helpers'
import { PolygonLayer, GeoJsonLayer, PathLayer } from '@deck.gl/layers/typed'
import { PathStyleExtension } from '@deck.gl/extensions'
import createMarkerLayer from './MarkerLayer'
import createInfoWindowHTML from './InfoWindow'
import useMapInteractions from './MapInteractions'
import TourMapActions from './Actions'
import map, { Map as MapboxMap, Marker } from 'react-map-gl/mapbox'
import 'mapbox-gl/dist/mapbox-gl.css'
import { decode } from 'google-polyline'
import { useAppDispatch } from 'planner-upgrades/redux/hooks'
import { getDepotCoordinates } from 'selectors/profile'
import { Icon } from 'common/components/base/.raw'

type ExtendedCollation = Collation & {
  tourId: string | number
  tourColor: string
  isSelected: boolean
}

interface InfoWindowData {
  collation: ExtendedCollation
}

export interface TourMapProps {
  showPolylines?: boolean
  clusterMarkers?: boolean
  fullscreen?: boolean
}

function hexToRgb(hex: string): Color {
  const arrByte = hex.match(/[0-9a-f]{2}/gi)
  if (!arrByte) return [0, 0, 0, 255]
  return [
    parseInt(arrByte[0], 16),
    parseInt(arrByte[1], 16),
    parseInt(arrByte[2], 16),
    255,
  ] as Color
}

const polylineCache: Map<string, number[][]> = new Map()

const TourMap: React.FC<TourMapProps> = ({
  showPolylines,
  clusterMarkers,
  fullscreen,
}) => {
  const dispatch = useAppDispatch()
  const deckRef = React.useRef<any>(null)

  const [isLoading, setIsLoading] = React.useState(false)

  const selectedTourId = useSelector(getSelectedTourId)
  const hiddenTourIds = useSelector(getHiddenTourIds)
  const tours: ShipmentTour[] = useSelector(getShipmentTours)

  const greenRegions = useSelector(fromGreenRegions.getGreenRegions) as {
    [key: string]: { id: number; pc3: number; polygon: any }
  }

  const depotCoordinates = useSelector(getDepotCoordinates)

  const INITIAL_VIEW_STATE: MapViewState = {
    longitude: depotCoordinates?.lng || 4.8790964,
    latitude: depotCoordinates?.lat || 52.076545,
    zoom: 10,
  }

  const [viewState, setViewState] =
    React.useState<MapViewState>(INITIAL_VIEW_STATE)

  const hidden = React.useMemo(() => {
    if (selectedTourId) {
      const result = tours
        .filter((tour) => String(tour.id) !== String(selectedTourId))
        .map((tour) => String(tour.id))
      return result
    }
    if (hiddenTourIds && hiddenTourIds.length > 0) {
      const result = hiddenTourIds.map(String)
      return result
    }
    return []
  }, [selectedTourId, tours, hiddenTourIds])

  const selectedMarkers = useSelector(getSelectedCollations)
  const [infoWindow, setInfoWindow] = React.useState<InfoWindowData | null>(
    null,
  )

  const allCollations = React.useMemo(() => {
    const hiddenSet = new Set(hidden)
    const selectedSet = new Set(selectedMarkers)

    const result: ExtendedCollation[] = []

    for (const tour of tours) {
      const tourIdStr = String(tour.id)

      if (hiddenSet.has(tourIdStr)) {
        continue
      }

      for (const collation of tour.collations) {
        if (!isValidCollation(collation)) continue

        const location = collation.recipient?.location
        if (!location?.latitude || !location?.longitude) continue

        result.push({
          ...collation,
          tourId: tour.id,
          tourColor: tour.color,
          isSelected: selectedSet.has(collation.id),
        })
      }
    }

    return result
  }, [tours, selectedMarkers, hidden])

  const collationsWithPaths = React.useMemo(() => {
    if (!showPolylines) return []

    return allCollations
      .filter((c) => c.polyline)
      .map((collation) => {
        try {
          const cachedPath = polylineCache.get(collation.polyline)
          if (cachedPath) {
            return { ...collation, decodedPath: cachedPath }
          }

          const decoded = decode(collation.polyline)
          const path = decoded.map((point: number[]) => [point[1], point[0]])
          polylineCache.set(collation.polyline, path)
          return { ...collation, decodedPath: path }
        } catch (err) {
          return { ...collation, decodedPath: [] }
        }
      })
  }, [allCollations, showPolylines])

  const tourOffsets = React.useMemo(() => {
    const uniqueTourIds = Array.from(
      new Set(allCollations.map((c) => c.tourId)),
    )
    const offsetMap: Record<string | number, number> = {}

    // offset polylines slightly for overlapping polylines
    const totalTours = uniqueTourIds.length
    uniqueTourIds.forEach((tourId, index) => {
      const offset = (index - (totalTours - 1) / 2) * 0.02
      offsetMap[tourId] = offset
    })

    return offsetMap
  }, [allCollations])

  const handleMoveToTour = React.useCallback(
    async (currentTourId: string, newTourId: string, collationId: string) => {
      setIsLoading(true)
      try {
        await Promise.race([
          dispatch(moveSingle(currentTourId, newTourId, collationId)),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout')), 10000),
          ),
        ])
      } catch (error) {
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch],
  )

  const handleMarkerClick = React.useCallback(
    (collation: ExtendedCollation) => {
      setInfoWindow({ collation })
    },
    [],
  )
  const handleHideTour = React.useCallback((tourId: string) => {
    dispatch(setHiddenTourIds(tourId))
  }, [])

  const markerLayer = React.useMemo(
    () =>
      createMarkerLayer({
        allCollations,
        selectedMarkers,
        onMarkerClick: handleMarkerClick,
      }),
    [allCollations, selectedMarkers, handleMarkerClick],
  )

  useMapInteractions({
    infoWindow,
    setInfoWindow,
    onMoveToTour: handleMoveToTour,
    onHideTour: handleHideTour,
  })

  const [selectionRect, setSelectionRect] = React.useState<number[][] | null>(
    null,
  )
  const dragStartPos = React.useRef<{
    coordinate: [number, number]
  } | null>(null)

  const onDragStart = React.useCallback((info: PickingInfo, event: any) => {
    if (event.srcEvent.shiftKey && info.coordinate) {
      dragStartPos.current = { coordinate: info.coordinate as [number, number] }
      setInfoWindow(null)
    }
  }, [])

  const onDrag = React.useCallback((info: PickingInfo) => {
    if (dragStartPos.current && info.coordinate) {
      const { coordinate } = dragStartPos.current
      const rect: number[][] = [
        coordinate,
        [info.coordinate[0], coordinate[1]],
        info.coordinate as [number, number],
        [coordinate[0], info.coordinate[1]],
      ]
      setSelectionRect(rect)
    }
  }, [])

  const onDragEnd = React.useCallback(
    (info: PickingInfo) => {
      if (dragStartPos.current && selectionRect && info.coordinate) {
        const { coordinate } = dragStartPos.current
        const minX = Math.min(coordinate[0], info.coordinate[0])
        const maxX = Math.max(coordinate[0], info.coordinate[0])
        const minY = Math.min(coordinate[1], info.coordinate[1])
        const maxY = Math.max(coordinate[1], info.coordinate[1])

        const selectedCollationIds = allCollations
          .filter((c) => {
            const lon = c.recipient.location.longitude
            const lat = c.recipient.location.latitude
            return lon >= minX && lon <= maxX && lat >= minY && lat <= maxY
          })
          .map((c) => c.id)

        dispatch(selectMultipleCollations(selectedCollationIds))
      }
      dragStartPos.current = null
      setSelectionRect(null)
    },
    [allCollations, dispatch, selectionRect],
  )

  const selectionLayer = React.useMemo(
    () =>
      selectionRect &&
      new PolygonLayer({
        id: 'selection-layer',
        data: [selectionRect],
        getPolygon: (d) => d,
        filled: true,
        stroked: true,
        getFillColor: [0, 0, 0, 20],
        getLineColor: [0, 0, 0, 255],
        getLineWidth: 1,
      }),
    [selectionRect],
  )

  const greenRegionsLayer = React.useMemo(
    () =>
      new GeoJsonLayer({
        id: 'green-regions',
        data: {
          type: 'FeatureCollection',
          features: Object.values(greenRegions).map((region) => ({
            type: 'feature',
            id: `GR-PC3-${region.pc3}`,
            geometry: region.polygon,
          })),
        },
        stroked: false,
        // getLineWidth: 1,
        // getLineColor: [0, 255, 0, 255],
        filled: true,
        getColor: [0, 255, 0, 0],
        lineWidthUnits: 'pixels',
        getFillColor: [0, 255, 0, 50],
        alphaCutoff: 0,
        clickable: false,
      }),
    [greenRegions],
  )

  const pathLayer = React.useMemo(
    () =>
      new PathLayer({
        id: 'PathLayer',
        data: collationsWithPaths,
        getColor: (d: any) => hexToRgb(d.tourColor),
        getWidth: 3,
        widthUnits: 'pixels',
        getPath: (d: any) => d.decodedPath,
        getOffset: (d: any) => tourOffsets[d.tourId] || 0,
        pickable: false,
        extensions: [new PathStyleExtension({ offset: true })] as any,
      }),
    [collationsWithPaths, tourOffsets],
  )

  const renderInfoWindow = React.useMemo(() => {
    if (!infoWindow) return null

    const { collation } = infoWindow
    const { longitude, latitude } = collation.recipient.location

    const viewport = new WebMercatorViewport(viewState)
    const [x, y] = viewport.project([longitude, latitude])

    const infoWindowStyle: React.CSSProperties = {
      position: 'absolute',
      left: x,
      top: y,
      transform: 'translate(-50%, -100%)',
      backgroundColor: 'white',
      padding: '10px',
      borderRadius: '5px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      zIndex: 1000,
    }

    return (
      <div style={infoWindowStyle}>
        <div
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={{
            __html: createInfoWindowHTML(
              collation,
              tours,
              handleMoveToTour,
              handleHideTour,
            ),
          }}
        />
      </div>
    )
  }, [infoWindow, viewState])

  const layers = React.useMemo(
    () =>
      [
        greenRegionsLayer,
        showPolylines ? pathLayer : null,
        markerLayer,
        selectionLayer,
      ].filter(Boolean),
    [greenRegionsLayer, showPolylines, pathLayer, markerLayer, selectionLayer],
  )

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: fullscreen ? '97%' : '100%',
      }}
    >
      {isLoading && (
        <>
          {/* Loading bar */}
          <div className="absolute top-0 left-0 w-full h-1 z-[1002] bg-gray-200/50 overflow-hidden">
            <div className="h-full w-1/4 bg-gradient-to-r from-transparent via-violet-50 to-transparent animate-loadingBar shadow-[0_0_12px_rgba(128,0,255,0.6)]" />
          </div>
          {/* Foggy overlay */}
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px] z-[1001] pointer-events-auto" />
        </>
      )}
      <DeckGL
        ref={deckRef}
        initialViewState={INITIAL_VIEW_STATE}
        onViewStateChange={
          ({ viewState: newViewState }) =>
            setViewState(newViewState as MapViewState) // Still track it for calculations
        }
        layers={layers}
        controller={{ dragRotate: false }}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        onClick={(info) => {
          if (!info.object) {
            setInfoWindow(null)
          }
        }}
        style={{ position: 'relative' }}
      >
        <MapboxMap
          reuseMaps
          mapboxAccessToken="pk.eyJ1IjoiYnJ1bGFudCIsImEiOiJjbWYxNmtiZXgwOXJrMnJzaXp2anFiMW95In0.n0WB4Ps35I93utaTuGPenQ"
          mapStyle="mapbox://styles/brulant/cmg5jh6xc009t01s7dm350qlz" // colors on, streets
          // mapStyle="mapbox://styles/brulant/cmgjj1hkw001e01s6cbtuepmo" // black and white
          style={{ position: 'absolute', width: '100%', height: '100%' }}
        >
          {depotCoordinates?.lng && depotCoordinates?.lat && (
            <Marker
              longitude={depotCoordinates.lng}
              latitude={depotCoordinates.lat}
              anchor="bottom"
            >
              <Icon name="subco" className="w-4 h-4 bg-green-50" />
            </Marker>
          )}
        </MapboxMap>
      </DeckGL>
      {renderInfoWindow}
      {selectedMarkers.length > 0 && (
        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-1.5 z-1000 pointer-events-auto">
          <TourMapActions setIsLoading={setIsLoading} />
        </div>
      )}
    </div>
  )
}

export default TourMap
